/* eslint-disable react/jsx-props-no-spreading */
import { useQuery } from '@tanstack/react-query';
import { type FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import CardArticle from '3-components/Cards/CardArticle/CardArticle';
import { resolveArticleListing } from '4-sections/FilteredListings/ArticleListing/resolvers/articleListingResolver';
import { getQueryURL, getPageQueryURL } from '3-components/ListingApp/ListingFilters/ListingFilterHelpers';
import ListingHeader from '3-components/ListingApp/ListingHeader/ListingHeader';
import ListingContent from '3-components/ListingApp/ListingContent/ListingContent';
import { ListingError, ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import { type ActiveFilter } from '3-components/ListingApp/ListingFilters/ListingFiltersTypes';
import { type ListingContextProps, ListingContext } from './ListingContext';
import { resolveEventsListing } from './EventsListing/resolvers/eventsListingResolver';

interface ListingProps {
    fetchUrl: string;
    type: string;
}

export const Listing: FC<ListingProps> = ({ fetchUrl, type }) => {
    const {
        activeFilters,
        setActiveFilters,
        listing: {
            filters,
            itemsPerPage = 10,
        },
        page,
        setPage,
        label,
    } = useContext(ListingContext) as ListingContextProps;

    const [itemCount, setItemCount] = useState(0);
    const [paginationBoundary, setPaginationBoundary] = useState({ start: 0, end: itemsPerPage });

    useEffect(() => {
        window.history.replaceState({}, '', getQueryURL(activeFilters, filters));

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilters]);

    const updateFilters = (value: ActiveFilter[]): void => {
        setPage(0);
        setActiveFilters(value);
    };

    const scrollToEl = useRef<HTMLDivElement>(null);

    const { data, error, isLoading, isError, isFetching } = useQuery(['news-safety'], () => {
        return fetch(fetchUrl).then(res => {
            return res.json();
        });
    }
    );

    const resolvedData = data
        ? type === 'article'
            ? resolveArticleListing(data, { minReadSuffix: 'mins read' })
            : resolveEventsListing(data)
        : undefined;

    const filteredItems = useMemo(() => {
        let filteredData = resolvedData ? [...resolvedData.items] : [];

        activeFilters.forEach(filter => {
            filteredData = filteredData.filter(item => {
                if (filter.values.length === 0) {
                    return item;
                }
                const filterValues = filter.values.map(value => {
                    return value.toLowerCase();
                });
                if (filter.field.toLowerCase() === 'search') {
                    const searchReg = new RegExp(`${filter.values.length > 0 ? filterValues : '.*'}`, 'gi');
                    if (searchReg.test(item.title) || searchReg.test(item.summary as string)) {
                        return item;
                    }
                } else if (item[filter.field]) {
                    const filterField = item[filter.field];
                    // Filters could either be in the form of an array, or a string. Check if it's an array.
                    const isFieldArray = Array.isArray(filterField);
                    if (isFieldArray) {
                        const lowerCasedFilterFieldArray = filterField.map((filterFieldValue: string) => {
                            return filterFieldValue.toLowerCase();
                        });
                        const isFilterInFilterField = filterValues.some(filterValue => {
                            return lowerCasedFilterFieldArray.includes(filterValue);
                        });
                        if (isFilterInFilterField) {
                            return item;
                        }
                    } else {
                        const lowerCasedFilterField = ((filterField as string)?.toLowerCase());
                        if (filterValues.includes(lowerCasedFilterField)) {
                            return item;
                        }
                    }
                }
                return false;
            });
        });

        return filteredData;
    }, [activeFilters, resolvedData]);

    useEffect(() => {
        setItemCount(filteredItems.length);
    }, [filteredItems]);


    useEffect(() => {
        if (scrollToEl?.current) {
            scrollToEl.current.scrollIntoView({ behavior: 'smooth' });
        }

        window.history.replaceState({}, '', getPageQueryURL(page));
    }, [page]);

    useEffect(() => {
        const startIndex = page === 0 ? 0 : page * itemsPerPage;
        const endIndex = page === 0 ? itemsPerPage - 1 : Math.min(((page + 1) * itemsPerPage) - 1, filteredItems.length);
        setPaginationBoundary(
            {
                start: startIndex,
                end: endIndex,
            }
        );
    }, [page, itemsPerPage, filteredItems.length]);

    if (isLoading) {
        return (
            <ListingLoader loadingMessage={label.loadingMessage} />
        );
    }

    if (isError || !data) {
        console.error(error);

        return (
            <ListingError errorMessage={label.errorMessage} />
        );
    }

    return (
        <div className="listing-app" ref={scrollToEl}>
            <div className="container">
                {label.title && (
                    <ListingHeader
                        filters={filters}
                        activeFilters={activeFilters}
                        setActiveFilters={updateFilters}
                        title={label.title}
                        clearFilterLabel={label.clearFilterButton}
                    />
                )}
                <ListingContent
                    isFetching={isFetching}
                    isNoItems={!filteredItems.length}
                    pagination={{
                        itemsPerPage,
                        page,
                        setPage,
                        totalItems: filteredItems ? itemCount : data.total,
                    }}
                    loadingLabel={label.loadingMessage}
                    noItemsLabel={label.noItemMessage}
                    cols="3"
                >
                    {filteredItems.map((props, idx) => {
                        if (idx >= paginationBoundary.start && idx <= paginationBoundary.end) {
                            return (
                                <CardArticle key={props.title} {...props} size={null} />
                            );
                        }
                        return null;
                    })}
                </ListingContent>
            </div>
        </div>
    );
};
